import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Is there an in-app entry behind this one?
 *
 * BrowserRouter stamps `idx` on every history entry it creates, starting at 0
 * for the page the app loaded on, so `idx > 0` means Back stays inside the app.
 * A router that does not write `window.history` (MemoryRouter in tests) leaves
 * no `idx` there; for that case the initial entry's key is always `'default'`.
 */
export function hasInAppHistory(locationKey: string): boolean {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  if (typeof idx === 'number') return idx > 0;
  return locationKey !== 'default';
}

/**
 * The Back control on a detail screen.
 *
 * Back buttons used to PUSH the list path. The list screens' own Back is
 * `navigate(-1)`, so history grew list → detail → list, and the list's Back
 * returned to the detail it had just left: a toggle, never an exit. It also
 * remounted the list fresh, which is where search and scroll went.
 *
 * So: go back one entry when there is one. When the screen was opened directly
 * (a link, a reload, a new tab) there is nothing behind it, and the fallback
 * REPLACES this entry rather than pushing, so the list's own Back then leaves.
 */
export function useBack(fallback: string, options?: { state?: unknown }) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = options?.state;
  return useCallback(() => {
    if (hasInAppHistory(location.key)) navigate(-1);
    else navigate(fallback, { replace: true, state });
  }, [navigate, location.key, fallback, state]);
}
